<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import maxLengthCodePoints from '@/support/customValidators/maxLengthCodePoints'
import {
  ALLOW_FUTURE_PUBLISHED_AT,
  isFuturePublishedAt,
  isNonExistentPublishedAt,
  maxPublishedAt
} from '@/support/utils/articleDate'

const IMAGE_WIDTH_MIN = 5
const IMAGE_WIDTH_MAX = 100
const IMAGE_WIDTH_STEP = 5

const IMAGE_WIDTH_PRESETS = [
  25,
  50,
  75,
  100
]

const RICH_TEXT_TOOLBAR = [
  [
    {
      header: [
        1,
        2,
        3,
        4,
        5,
        6,
        false
      ]
    }
  ],
  [
    'bold',
    'italic',
    'underline'
  ],
  [
    { list: 'ordered' },
    { list: 'bullet' }
  ],
  [
    { align: '' },
    { align: 'center' },
    { align: 'right' },
    { align: 'justify' }
  ],
  [
    'link',
    'image'
  ],
  [ 'clean' ]
]

const slugify = (input) => {
  if (!input) return ''

  return input
    .toString()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

const slugFromTitle = (title, currentSlug) => {
  const slug = slugify(title).replace(/^-+|-+$/g, '')

  return slug || currentSlug || `artigo-${Date.now()}`
}

export default {
  name: 'ArticleContentStep',
  components: {
    Datepicker: () => import('@/components/general/Datepicker'),
    InputField: () => import('@/components/general/InputField'),
    SelectField: () => import('@/components/general/SelectField'),
    TextareaField: () => import('@/components/general/TextareaField'),
    RichTextEditor: () => import('@/components/general/RichTextEditor'),
    Upload: () => import('@/components/general/Upload'),
    ArticleBodyPreview: () => import('./ArticleBodyPreview'),
    FormSection: () => import('@/components/general/FormSection')
  },

  props: {
    formData: {
      type: Object,
      required: true
    },

    themes: {
      type: Array,
      default: () => []
    },

    isEditing: {
      type: Boolean,
      default: false
    },

    canEditPublishedAt: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      RICH_TEXT_TOOLBAR,
      publishedAtMax: maxPublishedAt(),
      IMAGE_WIDTH_MIN,
      IMAGE_WIDTH_MAX,
      IMAGE_WIDTH_STEP,
      IMAGE_WIDTH_PRESETS,
      editorQuill: null,
      imageRange: null,
      imageWidth: null,
      imageWidthDraft: null,
      imageEl: null,
      imageBox: null,
      imageAlt: null,
      imageAltDraft: null,
      imageToolsHeight: 0,
      imageToolsWidth: 0,
      editorWidth: 0,
      imageDrag: null,
      imageDragWidth: null,
      previewAberto: false
    }
  },

  validations () {
    return {
      formData: {
        title: { required },
        subtitle: { required },
        slug: { required },
        id_theme: { required },
        publishedAt: {
          required: requiredIf(() => this.formData.status === 'published'),
          publishedAtExists: (value) => !isNonExistentPublishedAt(value),
          publishedAtNotFuture: (value) => ALLOW_FUTURE_PUBLISHED_AT || !isFuturePublishedAt(value)
        },

        author: { required },
        reading_time: { required },
        summary: {
          required,
          maxLength: maxLengthCodePoints(160)
        },

        body: { required },
        cover_image: { required },
        cover_image_title: { required },
        cover_image_caption: { required },
        cover_image_alt: {},
        card_image: { required },
        card_image_title: { required },
        card_image_caption: { required },
        card_image_alt: {}
      }
    }
  },

  computed: {
    imageWidthShown () {
      return this.imageDrag ? this.imageDragWidth : this.imageWidth
    },

    imageWidthDraftValue () {
      const bruto = parseInt(this.imageWidthDraft, 10)

      if (!Number.isFinite(bruto)) return null

      return Math.min(IMAGE_WIDTH_MAX, Math.max(IMAGE_WIDTH_MIN, bruto))
    },

    imageOutlineStyle () {
      const b = this.imageBox

      if (!b) return null

      return {
        top: b.top + 'px',
        left: b.left + 'px',
        width: b.width + 'px',
        height: b.height + 'px'
      }
    },

    imageToolsStyle () {
      const b = this.imageBox

      if (!b) return null

      const altura = this.imageToolsHeight || 0
      const largura = this.imageToolsWidth || 0
      const cabeAcima = b.top >= altura + 8
      const limite = Math.max(0, (this.editorWidth || 0) - largura)

      return {
        top: (cabeAcima ? b.top - altura - 8 : b.top + b.height + 8) + 'px',
        left: Math.min(Math.max(0, b.left), limite) + 'px'
      }
    },

    themesOptions () {
      return (this.themes || []).map(t => ({
        text: t.name,
        value: t.id
      }))
    },

    publishedAtHint () {
      return this.canEditPublishedAt
        ? this.$t('articles.form:published_at.hint')
        : this.$t('articles.form:published_at.hint.readonly')
    },

    updatedAtLabel () {
      const updatedAt = this.formData.userUpdatedAt || this.formData.updatedAt

      return updatedAt ? this.formatDateTime(updatedAt) : '-'
    }
  },

  beforeDestroy () {
    if (this.editorQuill) {
      this.editorQuill.root.removeEventListener('click', this.onEditorRootClick)
    }

    window.removeEventListener('scroll', this.updateImageBox, true)
    window.removeEventListener('resize', this.updateImageBox)
  },

  methods: {
    // eslint-disable-next-line vue/no-unused-properties -- called from parent via $refs.currentStep.validate()
    validate () {
      this.$v.$touch()

      return !this.$v.$invalid
    },

    emit (patch) {
      this.$emit('input', patch)
    },

    onTitleInput (value) {
      const patch = { title: value }

      if (!this.isEditing) {
        patch.slug = slugFromTitle(value, this.formData.slug)
      }

      this.emit(patch)
    },

    onSlugInput (value) {
      this.emit({ slug: slugify(value) })
    },

    onPublishedAtInput (value) {
      if (!this.canEditPublishedAt) return

      this.emit({ publishedAt: value || null })
    },

    onSubtitleInput (value) { this.emit({ subtitle: value }) },
    onAuthorInput (value) { this.emit({ author: value }) },
    onReadingTimeInput (value) {
      const parsed = value === '' || value === null ? null : Number(value)

      this.emit({ reading_time: Number.isFinite(parsed) ? parsed : null })
    },

    onSummaryInput (value) { this.emit({ summary: value }) },
    onBodyInput (value) {
      this.emit({ body: value })
      this.trackImageSelection(this.editorQuill && this.editorQuill.getSelection())
    },

    onThemeInput (value) {
      const id = value === '' || value === null || value === undefined ? null : Number(value)

      this.emit({ id_theme: Number.isFinite(id) ? id : null })
    },

    onCoverImageInput (file) { this.emit({ cover_image: file }) },
    onCoverImageTitleInput (value) { this.emit({ cover_image_title: value }) },
    onCoverImageCaptionInput (value) { this.emit({ cover_image_caption: value }) },
    onCoverImageAltInput (value) { this.emit({ cover_image_alt: value }) },

    onCardImageInput (file) { this.emit({ card_image: file }) },
    onCardImageTitleInput (value) { this.emit({ card_image_title: value }) },
    onCardImageCaptionInput (value) { this.emit({ card_image_caption: value }) },
    onCardImageAltInput (value) { this.emit({ card_image_alt: value }) },

    onEditorReady (quill) {
      this.editorQuill = quill
      quill.root.addEventListener('click', this.onEditorRootClick)
      window.addEventListener('scroll', this.updateImageBox, true)
      window.addEventListener('resize', this.updateImageBox)
      this.trackImageSelection(quill.getSelection())
    },

    onEditorRootClick (event) {
      const alvo = event.target
      const quill = this.editorQuill

      if (!quill || !alvo || alvo.tagName !== 'IMG') return

      const blot = quill.constructor.find(alvo)

      if (!blot) return

      const indice = quill.getIndex(blot)

      quill.setSelection(indice, 1, 'user')

      this.trackImageSelection({
        index: indice,
        length: 1
      })
    },

    onEditorSelectionChange (range) {
      if (!range) {
        setTimeout(() => {
          const barra = this.$el && this.$el.querySelector('.article-image-tools')

          if (!barra || !barra.contains(document.activeElement)) {
            this.trackImageSelection(null)
          }
        }, 0)

        return
      }

      this.trackImageSelection(range)
    },

    isImageRange (range) {
      const quill = this.editorQuill

      if (!quill || !range || range.length !== 1) return false

      const ops = quill.getContents(range.index, 1).ops || []
      const insert = ops.length === 1 ? ops[0].insert : null

      return !!(insert && typeof insert === 'object' && insert.image !== undefined)
    },

    trackImageSelection (range) {
      if (!this.isImageRange(range)) {
        this.imageRange = null
        this.imageWidth = null
        this.imageWidthDraft = null
        this.imageAlt = null
        this.imageAltDraft = null
        this.imageEl = null
        this.imageBox = null

        return
      }

      this.imageRange = range

      const formats = this.editorQuill.getFormat(range)

      this.imageWidth = this.parseImageWidth(formats.width)
      this.imageWidthDraft = this.imageWidth
      this.imageAlt = typeof formats.alt === 'string' ? formats.alt : null
      this.imageAltDraft = this.imageAlt

      const [ blot ] = this.editorQuill.getLeaf(range.index + 1)

      this.imageEl = blot && blot.domNode && blot.domNode.tagName === 'IMG'
        ? blot.domNode
        : this.editorQuill.root.querySelector('img')

      this.updateImageBox()
    },

    onImageHandleDown (event, canto) {
      const quill = this.editorQuill

      if (!quill || !this.imageEl || !this.imageBox) return

      const coluna = quill.root.clientWidth

      if (!coluna) return

      this.imageDrag = {
        canto,
        coluna,
        x: event.clientX,
        larguraInicial: this.imageBox.width,
        sinal: canto === 'ne' || canto === 'se' ? 1 : -1
      }

      this.imageDragWidth = Math.round((this.imageBox.width / coluna) * 100)

      window.addEventListener('mousemove', this.onImageHandleMove)
      window.addEventListener('mouseup', this.onImageHandleUp)
    },

    onImageHandleMove (event) {
      const arrasto = this.imageDrag

      if (!arrasto) return

      const delta = (event.clientX - arrasto.x) * arrasto.sinal
      const largura = arrasto.larguraInicial + delta
      const bruto = Math.round((largura / arrasto.coluna) * 100)
      const valor = Math.min(IMAGE_WIDTH_MAX, Math.max(IMAGE_WIDTH_MIN, bruto))

      this.imageDragWidth = valor
      this.imageEl.style.width = valor + '%'
      this.updateImageBox()
    },

    onImageHandleUp () {
      const valor = this.imageDragWidth

      window.removeEventListener('mousemove', this.onImageHandleMove)
      window.removeEventListener('mouseup', this.onImageHandleUp)

      if (!this.imageDrag) return

      this.imageDrag = null

      if (this.imageEl) {
        this.imageEl.style.width = ''
      }

      if (valor === null) return

      this.imageWidth = valor
      this.imageWidthDraft = valor
      this.applyImageWidth(valor + '%')
      this.imageDragWidth = null

      this.$nextTick(this.updateImageBox)
    },

    updateImageBox () {
      const wrap = this.$refs.bodyEditor

      if (!wrap || !this.imageEl || !this.imageRange) {
        this.imageBox = null

        return
      }

      const w = wrap.getBoundingClientRect()
      const i = this.imageEl.getBoundingClientRect()

      this.imageBox = {
        top: i.top - w.top,
        left: i.left - w.left,
        width: i.width,
        height: i.height
      }

      this.$nextTick(this.measureImageTools)
    },

    measureImageTools () {
      const barra = this.$refs.imageTools
      const wrap = this.$refs.bodyEditor

      if (barra) {
        this.imageToolsHeight = barra.offsetHeight
        this.imageToolsWidth = barra.offsetWidth
      }

      if (wrap) {
        this.editorWidth = wrap.clientWidth
      }
    },

    applyImageWidthPreset (valor) {
      this.imageWidth = valor
      this.imageWidthDraft = valor
      this.applyImageWidth(valor + '%')
      this.$nextTick(this.updateImageBox)
    },

    parseImageWidth (value) {
      if (typeof value !== 'string' || value.slice(-1) !== '%') return null

      const numero = parseInt(value, 10)

      return Number.isFinite(numero) ? numero : null
    },

    onImageWidthDraft (event) {
      this.imageWidthDraft = event.target.value
    },

    applyImageWidthDraft () {
      const valor = this.imageWidthDraftValue

      if (valor === null) {
        this.imageWidthDraft = this.imageWidth

        return
      }

      this.imageWidth = valor
      this.imageWidthDraft = valor
      this.applyImageWidth(valor + '%')
      this.$nextTick(this.updateImageBox)
    },

    clearImageWidth () {
      this.imageWidth = null
      this.imageWidthDraft = null
      this.applyImageWidth(false)
      this.$nextTick(this.updateImageBox)
    },

    onImageAltDraft (event) {
      this.imageAltDraft = event.target.value
    },

    applyImageAltDraft () {
      const valor = (this.imageAltDraft || '').trim()

      this.imageAlt = valor === '' ? null : valor
      this.imageAltDraft = this.imageAlt
      this.applyImageFormat('alt', valor === '' ? false : valor)
    },

    applyImageWidth (value) {
      this.applyImageFormat('width', value)
    },

    applyImageFormat (nome, value) {
      const quill = this.editorQuill
      const range = this.imageRange

      if (!quill || !this.isImageRange(range)) return

      quill.formatText(range.index, 1, nome, value, 'user')
      quill.setSelection(range.index, 1, 'silent')
    }
  }
}
</script>

<template>
  <form
    class="article-content-form"
    @submit.prevent
  >
    <FormSection>
      <InputField
        :label="$t('articles.form:title')"
        allow-emoji
        :value="formData.title"
        :validation="$v.formData.title"
        :counter="250"
        @input="onTitleInput"
      />
      <InputField
        :label="$t('articles.form:subtitle')"
        allow-emoji
        :value="formData.subtitle"
        :validation="$v.formData.subtitle"
        :counter="250"
        @input="onSubtitleInput"
      />
      <InputField
        :label="$t('articles.form:slug')"
        :value="formData.slug"
        :validation="$v.formData.slug"
        :counter="255"
        @input="onSlugInput"
      />
      <SelectField
        :label="$t('articles.form:theme')"
        :value="formData.id_theme"
        :validation="$v.formData.id_theme"
        :items="themesOptions"
        empty-option
        @input="onThemeInput"
      />
      <div class="article-meta-grid">
        <Datepicker
          class="article-meta-datepicker"
          :label="$t('articles.form:published_at')"
          :value="formData.publishedAt"
          :validation="$v.formData.publishedAt"
          :max="publishedAtMax"
          :disabled="!canEditPublishedAt"
          :hint="publishedAtHint"
          time
          @input="onPublishedAtInput"
        />
        <div class="article-meta-item">
          <span class="article-meta-label">{{ $t('articles.form:updated_at') }}</span>
          <span class="article-meta-value">{{ updatedAtLabel }}</span>
        </div>
      </div>
      <InputField
        :label="$t('articles.form:author')"
        allow-emoji
        :value="formData.author"
        :validation="$v.formData.author"
        :max-length="150"
        @input="onAuthorInput"
      />
      <InputField
        :label="$t('articles.form:reading_time')"
        :value="formData.reading_time"
        :validation="$v.formData.reading_time"
        type="number"
        @input="onReadingTimeInput"
      />
      <TextareaField
        :label="$t('articles.form:summary')"
        allow-emoji
        :value="formData.summary"
        :validation="$v.formData.summary"
        :counter="160"
        :auto-grow="true"
        @input="onSummaryInput"
      />
    </FormSection>
    <FormSection :title="$t('articles.form:section.body')">
      <div
        ref="bodyEditor"
        class="article-body-editor"
      >
        <RichTextEditor
          :value="formData.body"
          :validation="$v.formData.body"
          :toolbar="RICH_TEXT_TOOLBAR"
          @input="onBodyInput"
          @ready="onEditorReady"
          @selection-change="onEditorSelectionChange"
        />
        <template v-if="imageBox">
          <div
            class="article-image-tools__outline"
            :class="{ 'is-dragging': !!imageDrag }"
            :style="imageOutlineStyle"
          >
            <span
              v-for="canto in [ 'nw', 'ne', 'se', 'sw' ]"
              :key="canto"
              class="article-image-tools__handle"
              :class="'is-' + canto"
              @mousedown.prevent.stop="onImageHandleDown($event, canto)"
            />
            <span
              v-if="imageDrag"
              class="article-image-tools__medida"
              :class="'is-' + imageDrag.canto"
            >
              {{ imageWidthShown }}%
            </span>
          </div>
          <div
            ref="imageTools"
            class="article-image-tools"
            :style="imageToolsStyle"
            @mousedown.prevent
          >
            <div class="article-image-tools__linha">
              <span class="article-image-tools__label">
                {{ $t('articles.form:image.width') }}
              </span>
              <button
                v-for="preset in IMAGE_WIDTH_PRESETS"
                :key="preset"
                class="article-image-tools__preset"
                :class="{ 'is-active': imageWidth === preset }"
                type="button"
                @click="applyImageWidthPreset(preset)"
              >
                {{ preset }}%
              </button>
              <span class="article-image-tools__divisor" />
              <input
                class="article-image-tools__input"
                type="number"
                :min="IMAGE_WIDTH_MIN"
                :max="IMAGE_WIDTH_MAX"
                :step="IMAGE_WIDTH_STEP"
                :value="imageWidthDraft"
                :placeholder="IMAGE_WIDTH_MAX"
                :aria-label="$t('articles.form:image.width')"
                @mousedown.stop
                @input="onImageWidthDraft"
                @keyup.enter="applyImageWidthDraft"
              >
              <span class="article-image-tools__suffix">%</span>
              <button
                class="article-image-tools__reset"
                type="button"
                :disabled="imageWidth === null"
                @click="clearImageWidth"
              >
                {{ $t('articles.form:image.width.reset') }}
              </button>
            </div>
            <div class="article-image-tools__linha">
              <span class="article-image-tools__label">
                {{ $t('articles.form:image.alt.field') }}
              </span>
              <input
                class="article-image-tools__alt"
                type="text"
                :value="imageAltDraft"
                :placeholder="$t('articles.form:image.alt.placeholder')"
                :aria-label="$t('articles.form:image.alt.field')"
                maxlength="255"
                @mousedown.stop
                @input="onImageAltDraft"
                @keyup.enter="applyImageAltDraft"
              >
            </div>
          </div>
        </template>
      </div>
      <div class="article-body-preview-acao">
        <button
          class="article-body-preview-acao__botao"
          type="button"
          @click="previewAberto = true"
        >
          {{ $t('articles.form:preview.open') }}
        </button>
      </div>
      <ArticleBodyPreview
        :active="previewAberto"
        :body="formData.body"
        :article="formData"
        :themes="themes"
        @close="previewAberto = false"
      />
    </FormSection>
    <FormSection :title="$t('articles.form:section.cover')">
      <Upload
        :value="formData.cover_image"
        :validation="$v.formData.cover_image"
        icon="image-multiple"
        :label="$t('articles.form:cover_image')"
        :description="$t('articles.form:cover_image.description')"
        cropper
        :width="1200"
        :height="675"
        :preview="0.4"
        @input="onCoverImageInput"
      />
      <InputField
        :label="$t('articles.form:image.title')"
        allow-emoji
        :value="formData.cover_image_title"
        :validation="$v.formData.cover_image_title"
        :max-length="255"
        @input="onCoverImageTitleInput"
      />
      <InputField
        :label="$t('articles.form:image.caption')"
        allow-emoji
        :value="formData.cover_image_caption"
        :validation="$v.formData.cover_image_caption"
        :max-length="255"
        @input="onCoverImageCaptionInput"
      />
      <InputField
        :label="$t('articles.form:image.alt')"
        allow-emoji
        :value="formData.cover_image_alt"
        :validation="$v.formData.cover_image_alt"
        :max-length="255"
        @input="onCoverImageAltInput"
      />
    </FormSection>
    <FormSection :title="$t('articles.form:section.card')">
      <Upload
        :value="formData.card_image"
        :validation="$v.formData.card_image"
        icon="image-multiple"
        :label="$t('articles.form:card_image')"
        :description="$t('articles.form:card_image.description')"
        cropper
        :width="600"
        :height="600"
        :preview="0.5"
        @input="onCardImageInput"
      />
      <InputField
        :label="$t('articles.form:image.title')"
        allow-emoji
        :value="formData.card_image_title"
        :validation="$v.formData.card_image_title"
        :max-length="255"
        @input="onCardImageTitleInput"
      />
      <InputField
        :label="$t('articles.form:image.caption')"
        allow-emoji
        :value="formData.card_image_caption"
        :validation="$v.formData.card_image_caption"
        :max-length="255"
        @input="onCardImageCaptionInput"
      />
      <InputField
        :label="$t('articles.form:image.alt')"
        allow-emoji
        :value="formData.card_image_alt"
        :validation="$v.formData.card_image_alt"
        :max-length="255"
        @input="onCardImageAltInput"
      />
    </FormSection>
  </form>
</template>

<style
  lang="scss"
  scoped
>
.article-body-editor {
  position: relative;
}

.article-image-tools__outline {
  position: absolute;
  z-index: 2;
  border: 2px solid #005eb8;
  border-radius: 2px;
  pointer-events: none;
  box-shadow: inset 0 0 0 1px #ffffff, 0 0 0 1px #ffffff;

  &.is-dragging {
    cursor: nwse-resize;
  }
}

.article-image-tools__handle {
  position: absolute;
  width: 10px;
  height: 10px;
  border: 2px solid #005eb8;
  border-radius: 2px;
  background: #ffffff;
  pointer-events: auto;

  &::after {
    content: '';
    position: absolute;
    top: -6px;
    right: -6px;
    bottom: -6px;
    left: -6px;
  }

  &.is-nw {
    top: -7px;
    left: -7px;
    cursor: nwse-resize;
  }

  &.is-ne {
    top: -7px;
    right: -7px;
    cursor: nesw-resize;
  }

  &.is-se {
    right: -7px;
    bottom: -7px;
    cursor: nwse-resize;
  }

  &.is-sw {
    bottom: -7px;
    left: -7px;
    cursor: nesw-resize;
  }
}

.article-image-tools__medida {
  position: absolute;
  padding: 4px 10px;
  border-radius: 4px;
  background: rgba(0, 60, 113, 0.9);
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  pointer-events: none;

  &.is-nw,
  &.is-ne {
    top: 8px;
  }

  &.is-se,
  &.is-sw {
    bottom: 8px;
  }

  &.is-nw,
  &.is-sw {
    left: 8px;
  }

  &.is-ne,
  &.is-se {
    right: 8px;
  }
}

.article-body-preview-acao {
  margin-top: 12px;

  &__botao {
    padding: 7px 16px;
    border: 1px solid #005eb8;
    border-radius: 4px;
    background: transparent;
    font-size: 13px;
    font-weight: 700;
    color: #005eb8;
    cursor: pointer;

    &:hover {
      background: #005eb8;
      color: #ffffff;
    }
  }
}

.article-image-tools {
  position: absolute;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  white-space: nowrap;

  &__linha {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__label {
    width: 118px;
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #707070;
  }

  &__preset {
    padding: 3px 8px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: #f2f4f7;
    font-size: 12px;
    color: #364153;
    cursor: pointer;

    &:hover {
      background: #e3e9f2;
    }

    &.is-active {
      border-color: #005eb8;
      background: #005eb8;
      color: #ffffff;
    }
  }

  &__divisor {
    width: 1px;
    height: 18px;
    margin: 0 2px;
    background: #e0e0e0;
  }

  &__input {
    width: 52px;
    padding: 3px 4px;
    border: 1px solid #cccccc;
    border-radius: 4px;
    font-size: 12px;
    text-align: right;

    &:focus {
      border-color: #005eb8;
      outline: none;
    }
  }

  &__suffix {
    font-size: 12px;
    color: #707070;
  }

  &__aviso {
    font-size: 11px;
    font-style: italic;
    color: #d17b00;
  }

  &__alt {
    flex: 1 1 auto;
    min-width: 220px;
    padding: 3px 6px;
    border: 1px solid #cccccc;
    border-radius: 4px;
    font-size: 12px;

    &:focus {
      border-color: #005eb8;
      outline: none;
    }
  }

  &__apply {
    padding: 3px 10px;
    border: 1px solid #005eb8;
    border-radius: 4px;
    background: #005eb8;
    font-size: 12px;
    font-weight: 700;
    color: #ffffff;
    cursor: pointer;

    &:disabled {
      border-color: #dddddd;
      background: #f2f4f7;
      color: #aaaaaa;
      cursor: not-allowed;
    }
  }

  &__reset {
    padding: 3px 8px;
    border: 1px solid #cccccc;
    border-radius: 4px;
    background: transparent;
    font-size: 12px;
    color: #005eb8;
    cursor: pointer;

    &:disabled {
      color: #aaaaaa;
      cursor: not-allowed;
    }
  }
}
</style>
